import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '../../components/ui/Button.js';
import { Modal } from '../../components/ui/Modal.js';
import { useAbility } from '../../context/AbilityContext.js';
import { useAuth } from '../../context/AuthContext.js';
import { ApiError, api } from '../../services/api.js';

export interface ImpersonatableUser {
  _id: string;
  first_name: string;
  last_name: string;
  /** 331 (FR-008): the platform's own answer (`GET /users/:id`'s `is_administrator`): whether this user's roles
   * grant `manage User`. The panel never works it out from a role name. Missing (an older backend) means the control
   * shows as before; the server still refuses an administrator target with 409. */
  isAdministrator?: boolean;
}

/**
 * 036 US1 — start impersonating a customer from their detail page. Admin-only
 * (manage:User), and hidden for admin targets or self (US3 UX; the server also
 * refuses those). A confirm dialog names the target — no password re-entry
 * (FR-001a) — then POSTs to start and opens the returned handoff URL in the
 * user app, where the code is exchanged for an impersonation session.
 */
export function ImpersonateButton({ user }: { user: ImpersonatableUser }) {
  const ability = useAbility();
  const { user: currentUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');

  const start = useMutation({
    mutationFn: () => api.post<{ handoff_url: string }>('/auth/impersonate', { user_id: user._id }),
    onSuccess: (res) => {
      setError('');
      setOpen(false);
      window.open(res.handoff_url, '_blank');
    },
    onError: (err: unknown) => {
      setError(
        err instanceof ApiError || err instanceof Error ? err.message : 'Could not impersonate',
      );
    },
  });

  const isSelf = currentUser?._id === user._id;
  const isAdminTarget = user.isAdministrator === true;
  if (!ability.can('manage', 'User') || isSelf || isAdminTarget) return null;

  const fullName = `${user.first_name} ${user.last_name}`.trim();

  return (
    <>
      <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
        Impersonate
      </Button>
      <Modal open={open} onClose={() => setOpen(false)} title="Impersonate user" size="sm">
        <p className="text-sm text-ink">
          You are about to sign in as <span className="font-semibold">{fullName}</span> in the
          customer app. Every action is recorded and money-movement is blocked. You can exit at any
          time.
        </p>
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button loading={start.isPending} onClick={() => start.mutate()}>
            Start impersonation
          </Button>
        </div>
      </Modal>
    </>
  );
}
